import { FilestoreFile } from "../../api/filestore";
import { DataSource } from "../../types/dataSource";

export type AspectRatioKey = "1:1" | "2:1" | "1:2";

export type ProjectSize = "small" | "medium" | "large" | "x-large";

export interface CreateProjectData {
  name: string;
  description: string;
  propertyFiles: File | FilestoreFile | null;
  /** Property-boundary data sources (cadastre / parcel layers) pulled over
   *  the project area at creation; their polygons become properties next to
   *  any from `propertyFiles`. Any number, including none. */
  selectedPropertySources: DataSource[];
  fuelDisruptionFile: File | null;
  selectedLatLng: { lat: number; lng: number } | null;
  projectSize: ProjectSize;
  projectAspectRatio: AspectRatioKey;
  /** Additional tiles (beyond the base square at col=0,row=0) the user has
   *  expanded into, as origin-relative {col,row} offsets. North is +row, east
   *  is +col — matching the project-expansion grid. The project is created as
   *  the base square, then one /expand call is fired per entry. */
  expansionTiles: { col: number; row: number }[];
  vegetationSource: string;
  fireHistorySource: string;
  selectedAssetSources: DataSource[];
  selectedTreatmentSources: DataSource[];
  /** Stamp road corridors out of the vegetation layer. On by default. */
  stampRoadsIntoVegetation: boolean;
  /** Id of the `roads` data source cut out of the vegetation when stamping
   *  roads; "" = not yet chosen (the step pre-selects the built-in source,
   *  and the backend falls back to it too). */
  roadSource: string;
  /** Model the urban environment: cut building footprints out of vegetation,
   *  classify dense building blobs as Urban, and apply the default construction
   *  type. Only relevant when a buildings asset source is selected. */
  modelUrbanEnvironment: boolean;
  /** FK to ConstructionType applied as the project's default for buildings
   *  without their own construction_type. Required when modelUrbanEnvironment
   *  is on (defaults to the system "Wood" type); "" = not yet chosen. */
  defaultConstructionTypeId: number | "";
  tags?: string[];
  /** Listing folder the project is filed in on creation (null = top level). */
  folderId?: number | null;
}

export interface StepProps {
  data: CreateProjectData;
  onNext?: () => void;
  onBack?: () => void;
  onUpdateData: (updates: Partial<CreateProjectData>) => void;
}

// Bulk project creation types
export interface BulkProjectEntry {
  name: string;
  lat: number;
  lng: number;
  [key: string]: any; // Additional CSV columns
}

export interface BulkCreateProjectData {
  csvFile: File | null;
  parsedProjects: BulkProjectEntry[];
  projectSize: ProjectSize;
  projectAspectRatio: AspectRatioKey;
  vegetationSource: string;
  fireHistorySource: string;
  selectedAssetSources: DataSource[];
  selectedTreatmentSources: DataSource[];
  /** Property-boundary data sources pulled over every project's area at
   *  creation (bulk projects have no property file). */
  selectedPropertySources: DataSource[];
  /** Stamp road corridors out of the vegetation layer. On by default. */
  stampRoadsIntoVegetation: boolean;
  /** Id of the `roads` data source cut out of the vegetation when stamping
   *  roads; "" = not yet chosen (defaults to the built-in source). */
  roadSource: string;
  /** Model the urban environment: cut building footprints out of vegetation,
   *  classify dense building blobs as Urban, and apply the default construction
   *  type. Only relevant when a buildings asset source is selected. */
  modelUrbanEnvironment: boolean;
  /** FK to ConstructionType applied as the project's default for buildings
   *  without their own construction_type. Required when modelUrbanEnvironment
   *  is on (defaults to the system "Wood" type); "" = not yet chosen. */
  defaultConstructionTypeId: number | "";
  /** Listing folder every created project is filed in (null = top level). */
  folderId?: number | null;
}

/**
 * Detect whether an asset source represents buildings, based solely on its
 * `asset_type` field. The builtin "Buildings" source seeds
 * `asset_type = "Building"` (api/use_cases/init_data.py); custom sources set it
 * via their DataSource config.
 */
export const isBuildingsAssetSource = (s: DataSource): boolean =>
  (s.asset_type ?? "").toLowerCase().includes("building");

/**
 * Detect whether an asset source represents roads. Roads are not offered as an
 * ingestible asset in the create-project wizard — road handling is covered by
 * the "stamp roads into vegetation" processing option, which cuts the chosen
 * `roads` data source (the built-in Mapbox layer by default) out of the
 * vegetation.
 */
export const isRoadAssetSource = (s: DataSource): boolean =>
  (s.asset_type ?? "").toLowerCase().includes("road");

/** The `roadSourceIds` payload for project creation: the chosen roads source
 *  when stamping is on, else nothing (the backend attaches no road source). */
export const roadSourceIdsPayload = (data: {
  stampRoadsIntoVegetation: boolean;
  roadSource: string;
}): number[] =>
  data.stampRoadsIntoVegetation && data.roadSource !== "" ? [Number(data.roadSource)] : [];

export interface BulkStepProps {
  data: BulkCreateProjectData;
  onNext?: () => void;
  onBack?: () => void;
  onUpdateData: (updates: Partial<BulkCreateProjectData>) => void;
}
